import { useState } from "react";
import { SalaryBenchmark } from "@/components/shared/common/SalaryBenchmark";
import type { ProfileData, EditableTabProps } from "../../../lib/profileTypes";
import { SectionCard } from "../card/SectionCard";

interface SalaryTabProps extends EditableTabProps {
  profile: ProfileData;
  candidateId: number;
}

export function SalaryTab({
  profile,
  candidateId,
  editing,
  onEdit,
  onSave,
  onCancel,
  saving,
}: SalaryTabProps) {
  const [form, setForm] = useState({ ...profile });
  const f = (k: string) =>
    ((form as Record<string, unknown>)[k] as string) || "";
  const set = (k: string, v: string) => setForm({ ...form, [k]: v });
  const currency = profile.salary_currency || "PHP";

  if (!editing) {
    return (
      <div className="space-y-4">
        <SectionCard title="Salary Expectation" onEdit={onEdit}>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="font-semibold uppercase text-text-secondary">
                Currency
              </span>
              <span className="text-near-black">
                {profile.salary_currency || "—"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold uppercase text-text-secondary">
                Min
              </span>
              <span className="text-near-black">
                {profile.salary_min
                  ? `${currency} ${profile.salary_min.toLocaleString()}`
                  : "—"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold uppercase text-text-secondary">
                Max
              </span>
              <span className="text-near-black">
                {profile.salary_max
                  ? `${currency} ${profile.salary_max.toLocaleString()}`
                  : "—"}
              </span>
            </div>
          </div>
        </SectionCard>
        <div className="rounded-card bg-white p-4 shadow-card">
          <SalaryBenchmark candidateId={candidateId} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SectionCard title="Salary Expectation" editing>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <div className="flex items-center gap-2">
            <span className="font-semibold uppercase text-text-secondary">
              Currency
            </span>
            <input
              value={f("salary_currency") || "PHP"}
              onChange={(e) => set("salary_currency", e.target.value)}
              className="w-20 rounded-btn border border-border-card bg-white px-2.5 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="font-semibold uppercase text-text-secondary">
              Min
            </span>
            <input
              type="number"
              value={f("salary_min")}
              onChange={(e) => set("salary_min", e.target.value)}
              className="w-32 rounded-btn border border-border-card bg-white px-2.5 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="font-semibold uppercase text-text-secondary">
              Max
            </span>
            <input
              type="number"
              value={f("salary_max")}
              onChange={(e) => set("salary_max", e.target.value)}
              className="w-32 rounded-btn border border-border-card bg-white px-2.5 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
            />
          </div>
        </div>
      </SectionCard>
      <div className="flex gap-2">
        <button
          onClick={() =>
            onSave({
              salary_currency: form.salary_currency,
              salary_min: form.salary_min,
              salary_max: form.salary_max,
            })
          }
          disabled={saving}
          className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white hover:bg-rausch disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save"}
        </button>
        <button
          onClick={onCancel}
          className="rounded-btn px-4 py-2 text-sm font-medium text-text-secondary hover:text-near-black"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
